import Section from 'app/frontend/base/components/Section'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTeamApplications } from 'app/frontend/base/config/queries'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useCharacterDetailsStore } from 'app/frontend/base/helpers'
import { DataTable } from '@/components/ui/data-table'
import {
  date,
  role,
  characterName,
  status,
  links,
} from 'app/frontend/base/config/column-definitions/application'
import {
  itemLevel,
  mythicRating,
  raidParse,
  raidProgress,
} from 'app/frontend/base/config/column-definitions/character'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CircleCheck, CircleQuestionMark } from 'lucide-react'
import { isUnread } from './utils'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuth } from 'app/frontend/base/config/context/auth'

const TeamApplicants = () => {
  const { team, guild } = useTeam()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { accessLevel } = useAuth()
  const { detailsStore, fetchNewDetails, immediatelyFetchNewDetails } =
    useCharacterDetailsStore<wowaudit.Application>(
      (application) => application.characterReference?.id,
    )

  const hasHistoryAccess =
    accessLevel === 'admin' ||
    team.settings.recruitmentApplicationHistoryVisibility.value ===
      'entire_team'

  const { data: pendingApplications } = useTeamApplications(team.id, true)
  const { data: processedApplications, isPending } = useTeamApplications(
    team.id,
    false,
    hasHistoryAccess,
  )

  const pendingColumns: ColumnDef<wowaudit.Application>[] = useMemo(
    () => [
      date,
      role({ withFilters: false }),
      characterName({ withFilters: false }),
      itemLevel<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      raidProgress<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      raidParse<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      mythicRating<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      status({ withFilters: false }),
      links,
    ],
    [detailsStore],
  )

  const processedColumns: ColumnDef<wowaudit.Application>[] = useMemo(
    () => [
      date,
      role({ withFilters: true }),
      characterName({ withFilters: true }),
      itemLevel<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      raidProgress<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      raidParse<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      mythicRating<wowaudit.Application>(
        detailsStore,
        (application) => application.characterReference?.id,
      ),
      status({ withFilters: true }),
      links,
    ],
    [detailsStore],
  )

  const acceptedCount = useMemo(
    () =>
      processedApplications?.filter(
        (application) => application.status === 'accepted',
      ).length,
    [processedApplications],
  )

  const handleRowClick = (application: wowaudit.Application) => {
    if (isUnread(application)) {
      localStorage.setItem(`application_${application.id}_read`, 'true')
    }

    navigate(`${team.path}/applicants/${application.id}`, {
      state: {
        tableParams: Object.fromEntries(searchParams),
      },
    })
  }

  return (
    <Section title={`Applicants | ${team.name} | ${guild.name}`}>
      <h2 className='flex justify-between items-center'>
        <span className='flex items-center gap-3'>
          <span>{t('shared.applicants')}</span>
          <span className='bg-muted rounded-lg px-3 text-base'>
            {pendingApplications?.length}
          </span>
        </span>

        <div className='flex items-center gap-2'>
          {hasHistoryAccess ? (
            <div
              className={`flex items-center gap-1.5 text-muted-foreground text-base bg-background py-1.5 px-3 rounded-md transition-opacity ${
                isPending ? 'opacity-0' : ''
              }`}
            >
              <span
                className={
                  acceptedCount && acceptedCount > 0
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }
              >
                {acceptedCount ?? 0}
              </span>
              <span>/</span>
              <span>
                {(pendingApplications?.length || 0) +
                  (processedApplications?.length || 0)}
              </span>
              <span className='ml-1 text-muted-foreground font-normal text-sm'>
                accepted
              </span>
            </div>
          ) : null}

          <Link
            to={`${team.path}/apply`}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
          >
            View form
          </Link>
        </div>
      </h2>

      <div className='mb-6'>
        <div className='bg-muted rounded-md whitespace-nowrap'>
          <DataTable
            data={pendingApplications ?? []}
            columns={pendingColumns}
            includeHeader={true}
            onVisibleRowsChange={immediatelyFetchNewDetails}
            emptyMessage={() => (
              <div
                className={`flex flex-col items-center gap-4 my-8 ${
                  isPending ? 'opacity-0' : ''
                }`}
              >
                {processedApplications?.length ? (
                  <>
                    <CircleCheck size={48} className='text-primary' />{' '}
                    <span>There are no pending applications</span>
                  </>
                ) : (
                  <>
                    <CircleQuestionMark size={48} className='text-blue-500' />{' '}
                    <span>
                      Nobody has applied to this team yet. Visit the{' '}
                      <Link
                        to={`${team.path}/settings#recruitment`}
                        className='text-primary hover:text-primary/90 cursor-pointer'
                      >
                        recruitment settings
                      </Link>{' '}
                      to configure a form.
                    </span>
                  </>
                )}
              </div>
            )}
            sortingState={[{ id: 'date', desc: true }]}
            getRowId={(application) => application.id.toString()}
            onRowClick={handleRowClick}
            rowClassName={() => `hover:bg-background`}
          />
        </div>
      </div>

      {hasHistoryAccess ? (
        <div className='my-12'>
          {processedApplications?.length ? (
            <>
              <h2 className='text-xl font-semibold mb-3'>History</h2>
              <div className='bg-muted rounded-md whitespace-nowrap'>
                <DataTable
                  data={processedApplications ?? []}
                  columns={processedColumns}
                  includeHeader={true}
                  sortingState={[{ id: 'date', desc: true }]}
                  getRowId={(application) => application.id.toString()}
                  onVisibleRowsChange={fetchNewDetails}
                  pagination={20}
                  filterType='includesString'
                  onRowClick={handleRowClick}
                  rowClassName={() => 'hover:bg-background'}
                />
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </Section>
  )
}

export default TeamApplicants
